import "./CheckInDetailsDrawer.css";
import {
  FaBell,
  FaCalendarAlt,
  FaChevronDown,
  FaClock,
  FaHourglassHalf,
  FaPhone,
  FaVideo,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import EditAppointmentDrawer from "./EditAppointmentDrawer";
import {
  CM_APPOINTMENT_STATUS_OPTIONS,
  getCmBackendStatus,
  getCmStatusLabel,
  getCmStatusSlug,
} from "../utils/cmAppointmentStatus";
import { openWaitingRoomTab } from "../utils/videoSessionDevices";

const CANCELLED_STATUSES = [
  "canceled_by_user",
  "Canceled by Provider",
  "Canceled by Super Admin",
  "System Cancel",
  "late_cancelled",
  "late_cancel",
  "Cancelled",
];

const isCancelledAppointment = (appointment) =>
  appointment?.isCancelled || CANCELLED_STATUSES.includes(appointment?.status);

const getPatientInitials = (patient) => {
  const first = patient?.firstname?.[0] ?? "";
  const last = patient?.lastname?.[0] ?? "";
  const initials = `${first}${last}`.toUpperCase();
  return initials || "?";
};

const formatLongDate = (dateValue) =>
  dayjs(dateValue).format("dddd, MMMM D, YYYY");

const formatDisplayTime = (dateValue) => dayjs(dateValue).format("h:mm A");

const getAppointmentEndTime = (appointment) => {
  if (!appointment?.appointmenttime) return null;

  if (appointment.appointmentEndTime) {
    return new Date(appointment.appointmentEndTime);
  }

  const startTime = new Date(appointment.appointmenttime);
  const durationMinutes = appointment.sessionLength || 30;

  return new Date(startTime.getTime() + durationMinutes * 60000);
};

const hasAppointmentEnded = (appointment) => {
  const endTime = getAppointmentEndTime(appointment);
  if (!endTime || Number.isNaN(endTime.getTime())) return false;

  return new Date() >= endTime;
};

const canEditAppointmentDetails = (appointment) => {
  if (!appointment) return false;
  if (isCancelledAppointment(appointment)) return false;
  if (appointment.status === "Completed") return false;
  if (appointment.status === "No Show") return false;
  if (hasAppointmentEnded(appointment)) return false;
  return true;
};

const canStartVideoSession = (appointment, isVideo) => {
  if (!isVideo || !appointment) return false;
  if (isCancelledAppointment(appointment)) return false;
  if (appointment.status === "Completed" || appointment.status === "No Show") {
    return false;
  }
  if (hasAppointmentEnded(appointment)) return false;

  return true;
};

const CheckInDetailsDrawer = ({
  appointment,
  displayStatus,
  fetchAppointmentData,
  onClose,
}) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user);
  const statusDropdownRef = useRef(null);
  const pendingCloseActionRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);

  const initialMethod = appointment?.appointmentMethod || "video";
  const initialStatus = useMemo(
    () => getCmStatusLabel(appointment, displayStatus),
    [appointment, displayStatus],
  );

  const [status, setStatus] = useState(initialStatus);

  const isVideo = initialMethod === "video";
  const isStartVideoSessionEnabled = canStartVideoSession(appointment, isVideo);
  const canEdit = canEditAppointmentDetails(appointment);
  const patient = appointment?.user;
  const patientName = `${patient?.firstname ?? ""} ${
    patient?.lastname ?? ""
  }`.trim();
  const selectedStatusSlug = getCmStatusSlug(status);

  useEffect(() => {
    setShowEditDrawer(false);
    setIsStatusOpen(false);
    setShowUnsavedModal(false);
    pendingCloseActionRef.current = null;
    setStatus(getCmStatusLabel(appointment, displayStatus));
  }, [appointment?._id, displayStatus, appointment]);

  useEffect(() => {
    if (!isStatusOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isStatusOpen]);

  const hasStatusChanges = useMemo(
    () => status !== initialStatus,
    [initialStatus, status],
  );

  const shouldConfirmDiscard = hasStatusChanges && !showEditDrawer;

  useEffect(() => {
    if (!shouldConfirmDiscard) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldConfirmDiscard]);

  const saveStatus = async () => {
    if (!hasStatusChanges || isSaving) {
      return !hasStatusChanges;
    }

    const authToken = user.user?.token;
    setIsSaving(true);

    try {
      const statusResponse = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointment._id}`,
        { status: getCmBackendStatus(status) },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );

      if (!statusResponse.data.success) {
        toast.error("Failed to update appointment status.");
        return false;
      }

      toast.success("Appointment status updated successfully.");
      fetchAppointmentData?.();
      return true;
    } catch (error) {
      console.error("Error saving appointment status:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save appointment status.",
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const completePendingCloseActionAfterSave = () => {
    const pendingAction = pendingCloseActionRef.current;
    pendingCloseActionRef.current = null;
    setShowUnsavedModal(false);

    if (pendingAction?.type === "navigate") {
      onClose();
      navigate(pendingAction.to, pendingAction.options);
      return;
    }

    onClose();
  };

  const requestClose = (pendingAction = { type: "close" }) => {
    if (!shouldConfirmDiscard) {
      if (pendingAction.type === "navigate") {
        onClose();
        navigate(pendingAction.to, pendingAction.options);
        return;
      }

      onClose();
      return;
    }

    pendingCloseActionRef.current = pendingAction;
    setShowUnsavedModal(true);
  };

  const dismissUnsavedModal = () => {
    pendingCloseActionRef.current = null;
    setShowUnsavedModal(false);
  };

  const handleClose = () => {
    requestClose({ type: "close" });
  };

  const handleDiscardChanges = () => {
    pendingCloseActionRef.current = null;
    setShowUnsavedModal(false);
    onClose();
  };

  const handleConfirmSave = async () => {
    const saved = await saveStatus();
    if (!saved) return;

    completePendingCloseActionAfterSave();
  };

  const handleSaveStatus = async () => {
    const saved = await saveStatus();
    if (saved) {
      onClose();
    }
  };

  const handleStartVideoSession = () => {
    if (!isStartVideoSessionEnabled) return;

    if (!openWaitingRoomTab(appointment)) {
      toast.error(
        "Unable to open waiting room in a new tab. Please allow pop-ups and try again.",
      );
      return;
    }

    // Keep schedule/app tab intact; only close the details drawer.
    onClose();
  };

  const handleSendReminder = async () => {
    setIsSendingReminder(true);

    try {
      toast.success(`Reminder sent to ${patientName || "patient"}.`);
    } finally {
      setIsSendingReminder(false);
    }
  };

  if (!appointment) return null;

  return (
    <>
      <div
        className="cm-details-drawer-overlay"
        onClick={handleClose}
        role="presentation"
      >
        <aside
          aria-label="Check-in details"
          className="cm-details-drawer"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="cm-details-drawer-header">
            <h2 className="cm-details-drawer-title">Check-in Details</h2>
            <button
              className="cm-details-drawer-close"
              onClick={handleClose}
              type="button"
            >
              Close
            </button>
          </div>

          <div
            className={`cm-details-drawer-actions${
              !isVideo ? " cm-details-drawer-actions--single" : ""
            }`}
          >
            {isVideo && (
              <button
                className="cm-details-btn-primary"
                disabled={!isStartVideoSessionEnabled}
                onClick={handleStartVideoSession}
                type="button"
              >
                <FaVideo />
                Start Video Session
              </button>
            )}

            <button
              className="cm-details-btn-secondary"
              disabled={isSendingReminder}
              onClick={handleSendReminder}
              type="button"
            >
              <FaBell />
              {isSendingReminder ? "Sending..." : "Send Reminder"}
            </button>
          </div>

          <div className="cm-details-drawer-body">
            <div className="cm-details-patient">
              <div className="cm-details-avatar">
                {getPatientInitials(patient)}
              </div>
              <h3 className="cm-details-patient-name">
                {patientName || "Unknown Patient"}
              </h3>
            </div>

            <div className="cm-status-dropdown" ref={statusDropdownRef}>
              <button
                aria-expanded={isStatusOpen}
                aria-haspopup="listbox"
                className={`cm-status-selected cm-status-chip status-${selectedStatusSlug}`}
                onClick={() => setIsStatusOpen((open) => !open)}
                type="button"
              >
                <span>{status}</span>
                <FaChevronDown
                  className={`cm-status-chevron${isStatusOpen ? " is-open" : ""}`}
                />
              </button>

              {isStatusOpen && (
                <ul className="cm-status-options" role="listbox">
                  {CM_APPOINTMENT_STATUS_OPTIONS.map((option) => (
                    <li key={option.label} role="presentation">
                      <button
                        aria-selected={status === option.label}
                        className={`cm-status-option cm-status-chip status-${option.slug}${
                          status === option.label ? " is-selected" : ""
                        }`}
                        onClick={() => {
                          setStatus(option.label);
                          setIsStatusOpen(false);
                        }}
                        role="option"
                        type="button"
                      >
                        <span>{option.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <ul className="cm-details-meta-list">
              <li className="cm-details-meta-item">
                <FaCalendarAlt className="cm-details-meta-icon" />
                <span className="cm-details-meta-value">
                  {formatLongDate(appointment.appointmenttime)}
                </span>
              </li>

              <li className="cm-details-meta-item">
                <FaClock className="cm-details-meta-icon" />
                <span className="cm-details-meta-value">
                  {formatDisplayTime(appointment.appointmenttime)}
                </span>
              </li>

              <li className="cm-details-meta-item">
                <FaHourglassHalf className="cm-details-meta-icon" />
                <span className="cm-details-meta-value">
                  {appointment.sessionLength || 30} minutes
                </span>
              </li>

              <li className="cm-details-meta-item">
                {isVideo ? (
                  <FaVideo className="cm-details-meta-icon" />
                ) : (
                  <FaPhone className="cm-details-meta-icon" />
                )}
                <span className="cm-details-meta-value">
                  {isVideo ? "Video" : "Phone"}
                </span>
              </li>

              {!isVideo && (
                <li className="cm-details-meta-item">
                  <FaPhone className="cm-details-meta-icon" />
                  <span className="cm-details-meta-value">
                    {patient?.phoneno || "Not available"}
                  </span>
                </li>
              )}
            </ul>
          </div>

          <div className="cm-details-drawer-footer">
            <button
              className="cm-details-btn-edit"
              disabled={!canEdit}
              onClick={() => setShowEditDrawer(true)}
              type="button"
            >
              Edit
            </button>

            <button
              className={`cm-details-btn-save${
                hasStatusChanges ? " is-active" : ""
              }`}
              disabled={!hasStatusChanges || isSaving}
              onClick={handleSaveStatus}
              type="button"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </aside>
      </div>

      {showEditDrawer && (
        <EditAppointmentDrawer
          appointment={appointment}
          fetchAppointmentData={fetchAppointmentData}
          onCancel={() => setShowEditDrawer(false)}
          onSaved={() => {
            setShowEditDrawer(false);
            onClose();
          }}
        />
      )}

      {showUnsavedModal && (
        <div
          className="cm-details-unsaved-modal-overlay"
          onClick={dismissUnsavedModal}
          role="presentation"
        >
          <div
            aria-labelledby="cm-details-unsaved-modal-title"
            aria-modal="true"
            className="cm-details-unsaved-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
          >
            <div className="cm-details-unsaved-modal-header">
              <h3
                className="cm-details-unsaved-modal-title"
                id="cm-details-unsaved-modal-title"
              >
                Save Changes?
              </h3>
              <button
                aria-label="Close dialog"
                className="cm-details-unsaved-modal-close"
                onClick={dismissUnsavedModal}
                type="button"
              >
                &times;
              </button>
            </div>

            <p className="cm-details-unsaved-modal-message">
              If you close this appointment without saving, all changes will be
              lost.
            </p>

            <div className="cm-details-unsaved-modal-footer">
              <button
                className="cm-details-unsaved-modal-btn-discard"
                disabled={isSaving}
                onClick={handleDiscardChanges}
                type="button"
              >
                Discard
              </button>
              <button
                className="cm-details-unsaved-modal-btn-save"
                disabled={isSaving}
                onClick={handleConfirmSave}
                type="button"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CheckInDetailsDrawer;
